import { TIME_FORMAT } from '@/constants/global.constants';
import useError from '@/hooks/useError';
import useFilters from '@/hooks/useFilters';
import { ChevronDownIcon, FileTextIcon } from '@/icons/index';
import { cn } from '@/lib/cn';
import type { TableProps } from 'antd';
import { Button, Collapse, Empty, Flex, Spin } from 'antd';
import dayjs from 'dayjs';
import type { ReactNode } from 'react';
import CustomDrawer from '../CustomDrawer';
import CustomPagination from '../CustomPagination';
import CustomTable from '../CustomTable';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Field name mapping for the jobs list.
 * Allows adapting to different API response shapes.
 */
export interface JobsFieldMap {
  /** Field name for the job's unique ID. Default: "jobId" */
  id?: string;
  /** Field name for the uploaded file name. Default: "fileName" */
  fileName?: string;
  /** Field name for the job creation timestamp. Default: "createdAt" */
  createdAt?: string;
}

const DEFAULT_JOBS_FIELD_MAP: Required<JobsFieldMap> = {
  id: 'jobId',
  fileName: 'fileName',
  createdAt: 'createdAt',
};

/**
 * Minimal hook return type expected from RTK Query hooks.
 */
type QueryResult = {
  data?: any;
  error?: any;
  isLoading: boolean;
  isFetching?: boolean;
};

/**
 * Hook signature for fetching failed jobs.
 */
type UseJobsQueryHook = (args: {
  params: { page: number; limit: number };
}) => QueryResult;

/**
 * Hook signature for fetching failed records per job.
 */
type UseRecordsQueryHook = (
  args: { params: { page: number; limit: number; job_id?: string } },
  options?: { skip?: boolean },
) => QueryResult;

export type RecordsFieldMap = {
  total?: string;
  data?: string;
};

const DEFAULT_RECORDS_FIELD_MAP: Required<RecordsFieldMap> = {
  total: 'meta.total',
  data: 'data',
};

/**
 * Props for the BulkUploadErrors component.
 */
export interface BulkUploadErrorsProps<
  TRecord extends Record<string, any> = any,
> {
  /** Whether the error drawer is open */
  open: boolean;
  /** Close callback */
  onClose: () => void;
  /** Drawer title */
  title?: string;
  /** Drawer width */
  drawerWidth?: number;
  /** RTK Query hook to fetch failed import jobs (paginated) */
  useGetJobsQuery: UseJobsQueryHook;
  /** Field name mapping for the jobs response */
  jobsFieldMap?: JobsFieldMap;
  /** Error message shown when jobs query fails */
  jobsErrorMessage?: string;
  /** RTK Query hook to fetch failed records for a specific job (paginated) */
  useGetRecordsQuery: UseRecordsQueryHook;
  /** Error message shown when records query fails */
  recordsErrorMessage?: string;
  /** Field name mapping for the records response */
  recordsFieldMap?: RecordsFieldMap;
  /** Module-specific table columns for the failed records */
  columns: TableProps<TRecord>['columns'];
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

/**
 * A self-contained bulk upload error viewer.
 *
 * Handles everything:
 * - Drawer wrapper
 * - Jobs list with pagination and collapsible panels
 * - Inner table with pagination and module-specific columns
 *
 * The caller only provides: API hooks + columns.
 */
export default function BulkUploadErrors<
  TRecord extends Record<string, any> = any,
>({
  open,
  onClose,
  title = 'File Upload Error',
  drawerWidth = 1000,
  useGetJobsQuery,
  jobsFieldMap,
  jobsErrorMessage = 'Failed to get upload error data',
  useGetRecordsQuery,
  recordsErrorMessage = 'Failed to get error records',
  recordsFieldMap,
  columns,
}: BulkUploadErrorsProps<TRecord>) {
  const fieldMap = { ...DEFAULT_JOBS_FIELD_MAP, ...jobsFieldMap };
  const { page, limit, handlePagination } = useFilters<any>({
    defaultLimit: 5,
  });

  const {
    data: jobData,
    error,
    isLoading,
  } = useGetJobsQuery({
    params: { page, limit },
  });
  useError(error, jobsErrorMessage);

  const jobDataList = jobData?.data?.data || [];
  const jobTotalData = jobData?.data?.meta?.total || 0;

  return (
    <CustomDrawer
      width={drawerWidth}
      open={open}
      onClose={onClose}
      title={title}
    >
      <JobsList
        list={jobDataList}
        fieldMap={fieldMap}
        totalList={jobTotalData}
        limit={limit}
        page={page}
        handlePagination={handlePagination}
        loading={isLoading}
        useGetRecordsQuery={useGetRecordsQuery}
        recordsErrorMessage={recordsErrorMessage}
        recordsFieldMap={recordsFieldMap}
        columns={columns}
      />
    </CustomDrawer>
  );
}

// ---------------------------------------------------------------------------
// Jobs List (collapsible panels)
// ---------------------------------------------------------------------------

interface JobsListProps<TRecord extends Record<string, any>> {
  list: any[];
  fieldMap: Required<JobsFieldMap>;
  totalList: number;
  limit: number;
  page: number;
  handlePagination: (page: number, limit: number) => void;
  loading: boolean;
  useGetRecordsQuery: UseRecordsQueryHook;
  recordsErrorMessage: string;
  recordsFieldMap?: RecordsFieldMap;
  columns: TableProps<TRecord>['columns'];
}

function JobsList<TRecord extends Record<string, any>>({
  list,
  fieldMap,
  totalList,
  limit,
  page,
  handlePagination,
  loading,
  useGetRecordsQuery,
  recordsErrorMessage,
  recordsFieldMap,
  columns,
}: JobsListProps<TRecord>) {
  if (loading) {
    return (
      <Flex align="center" justify="center" className="py-14">
        <Spin />
      </Flex>
    );
  }

  if (!list?.length) {
    return <Empty description="No failed uploads found" />;
  }

  return (
    <div className="change-pagination">
      <Collapse
        defaultActiveKey="0"
        expandIconPosition="end"
        bordered={false}
        accordion
        expandIcon={({ isActive }) => (
          <Button
            size="small"
            color="default"
            variant="text"
            className="rounded shrink-0 !border-theme-gray-700 !bg-transparent"
            classNames={{ icon: 'flex items-center justify-center' }}
            icon={
              <span
                className={cn(
                  'transition-transform duration-300',
                  isActive ? 'rotate-180' : 'rotate-0',
                )}
              >
                <ChevronDownIcon size={20} />
              </span>
            }
          />
        )}
        items={list.map((job, idx) => ({
          key: `${idx}`,
          label: (
            <Flex align="center" gap={10}>
              <div className="w-8 h-8 rounded-lg bg-theme-primary/10 flex items-center justify-center shrink-0">
                <FileTextIcon size={16} className="text-theme-primary" />
              </div>
              <span className="md:text-white font-medium text-base">
                {String(job?.[fieldMap.fileName] || 'Uploaded-file')} (
                {dayjs(job?.[fieldMap.createdAt]).format(
                  `MMM DD, YYYY ${TIME_FORMAT}`,
                )}
                )
              </span>
            </Flex>
          ),
          children: (
            <RecordsTable
              jobId={job?.[fieldMap.id]}
              useGetRecordsQuery={useGetRecordsQuery}
              recordsErrorMessage={recordsErrorMessage}
              recordsFieldMap={recordsFieldMap}
              columns={columns}
            />
          ),
        }))}
        className="[&_.ant-collapse-header]:!px-0 [&_.ant-collapse-content-box]:!p-0"
      />

      {totalList > 0 && (
        <CustomPagination
          total={totalList}
          limit={limit}
          page={page}
          handlePagination={handlePagination}
          className="px-0 mt-6"
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Records Table (inner table per job)
// ---------------------------------------------------------------------------

interface RecordsTableProps<TRecord extends Record<string, any>> {
  jobId?: string;
  useGetRecordsQuery: UseRecordsQueryHook;
  recordsErrorMessage: string;
  recordsFieldMap?: RecordsFieldMap;
  columns: TableProps<TRecord>['columns'];
}

function RecordsTable<TRecord extends Record<string, any>>({
  jobId,
  useGetRecordsQuery,
  recordsErrorMessage,
  recordsFieldMap,
  columns,
}: RecordsTableProps<TRecord>): ReactNode {
  const { page, limit, handlePagination } = useFilters({
    defaultLimit: 5,
  });

  const { data, isLoading, isFetching, error } = useGetRecordsQuery(
    {
      params: {
        page,
        limit,
        job_id: jobId,
      },
    },
    {
      skip: !jobId,
    },
  );
  useError(error, recordsErrorMessage);

  const getNestedValue = (obj: any, path: string) => {
    return path.split('.').reduce((acc, part) => acc?.[part], obj);
  };

  const totalProp = recordsFieldMap?.total || DEFAULT_RECORDS_FIELD_MAP.total;
  const dataProp = recordsFieldMap?.data || DEFAULT_RECORDS_FIELD_MAP.data;

  return (
    <CustomTable
      page={page}
      limit={limit}
      hideRowSelect
      columns={columns ?? []}
      dataSource={getNestedValue(data?.data, dataProp) ?? []}
      onPaginate={handlePagination}
      loading={isLoading || (isFetching ?? false)}
      total={getNestedValue(data?.data, totalProp) ?? 0}
      className="px-6"
      rootClassName="p-0 m-0 border-none mb-6"
      tableRootClassName="p-0 border-none"
    />
  );
}
